import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import { allVideos, sortVideos, type SortKey } from "@/lib/videoData";
import { usePageMeta } from "@/hooks/usePageMeta";

const hotKeywords = ["悬疑", "古装", "综艺", "科幻", "家庭", "真人秀"];

const categoryChips = [
  { key: "all", label: "全部类型" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

const sortChips: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "hot", label: "最热" },
  { key: "rating", label: "评分" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const [keyword, setKeyword] = useState(query);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState<SortKey>("latest");

  usePageMeta(
    query ? `${query} 搜索结果 - 糖豆视频` : "搜索影视综艺 - 糖豆视频",
    query
      ? `糖豆视频为你找到与「${query}」相关的影视综艺内容，支持按类型与排序进一步筛选，快速定位想看的影片。`
      : "在糖豆视频搜索电影、电视剧与综艺，输入片名、演员或题材关键词即可快速查找高清影视内容。",
    query ? `${query},搜索结果,糖豆视频,影视搜索` : "糖豆视频搜索,影视搜索,电影搜索,综艺搜索",
  );

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(() => {
    const base = query
      ? allVideos.filter((video) => {
          const text = [
            video.title,
            video.director,
            video.region,
            video.tagline,
            video.description,
            ...video.actors,
            ...video.genre,
          ]
            .join(" ")
            .toLowerCase();
          return text.includes(query.toLowerCase());
        })
      : [];
    const typed = category === "all" ? base : base.filter((video) => video.category === category);
    return sortVideos(typed, sort);
  }, [query, category, sort]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: value });
    setCategory("all");
  };

  const runKeyword = (value: string) => {
    setKeyword(value);
    setSearchParams({ q: value });
    setCategory("all");
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-100">
      <Navbar />
      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200/80 bg-background-50 py-8 md:py-10">
          <div className="mx-auto w-full max-w-[1360px] px-4 md:px-8">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="transition-colors hover:text-primary-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="font-medium text-foreground-800">搜索结果</span>
            </nav>

            <h1 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-[32px]">
              {query ? `「${query}」的搜索结果` : "搜索影视综艺内容"}
            </h1>
            <p className="mt-1.5 text-sm text-foreground-600">
              {query ? "可按类型与排序进一步缩小范围，结果实时更新。" : "输入片名、演员或题材关键词，例如「悬疑」「古装」「真人秀」。"}
            </p>

            <form onSubmit={submitSearch} className="mt-5 flex w-full max-w-2xl items-center gap-2" role="search">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索电影、电视剧、综艺"
                  aria-label="搜索关键词"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-100 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-200/70"
                />
              </div>
              <button
                type="submit"
                className="flex h-11 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-800 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-900"
              >
                <i className="ri-search-line text-base" />
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-500">热门搜索</span>
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => runKeyword(word)}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1360px] px-4 py-6 md:px-8 md:py-8">
          {query ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-foreground-700">
                  找到 <span className="font-heading text-lg font-extrabold text-primary-800">{results.length}</span> 个与
                  「{query}」相关的结果
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {categoryChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setCategory(chip.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
                        category === chip.key
                          ? "bg-primary-800 text-background-50"
                          : "bg-background-50 text-foreground-600 hover:text-primary-700"
                      }`}
                    >
                      {chip.label}
                    </button>
                  ))}
                  <span className="mx-1 h-5 w-px bg-background-300" />
                  {sortChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setSort(chip.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
                        sort === chip.key
                          ? "bg-accent-500 text-foreground-950"
                          : "bg-background-50 text-foreground-600 hover:text-primary-700"
                      }`}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>

              {results.length > 0 ? (
                <div className="mt-5 grid grid-cols-2 gap-3.5 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-6">
                  {results.map((video) => (
                    <VideoCard key={video.id} video={video} showTagline={false} />
                  ))}
                </div>
              ) : (
                <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-emotion-unhappy-line text-2xl" />
                  </span>
                  <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
                    抱歉，未找到相关视频
                  </p>
                  <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-foreground-500">
                    换个关键词试试，或返回首页浏览精选内容。也可以搜索「悬疑」「古装」「综艺」等题材。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <Link
                      to="/"
                      className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-800 px-4 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-900"
                    >
                      <i className="ri-home-4-line text-sm" />
                      返回首页
                    </Link>
                    <button
                      type="button"
                      onClick={() => setCategory("all")}
                      className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      <i className="ri-refresh-line text-sm" />
                      清除类型筛选
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {[
                { icon: "ri-film-line", title: "电影片库", text: "悬疑、剧情、科幻等题材精选电影", to: "/category/movie" },
                { icon: "ri-tv-2-line", title: "剧集频道", text: "热播剧集与全集回顾，追更不错过", to: "/category/tv" },
                { icon: "ri-mic-line", title: "综艺专区", text: "真人秀、音乐竞演与脱口秀合集", to: "/category/variety" },
              ].map((item) => (
                <Link
                  key={item.title}
                  to={item.to}
                  className="group flex cursor-pointer items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700 transition-colors group-hover:bg-primary-800 group-hover:text-accent-300">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{item.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.text}</p>
                    <span className="mt-2 flex items-center gap-1 text-[11px] font-medium text-primary-700">
                      去逛逛
                      <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}